/**
 * Audit-Trail Export (Spec 530, Phase 5, P5).
 *
 * JSON- und CSV-Download der VOLLSTAENDIGEN abgefragten Ergebnismenge (Cursor bis
 * Ende via {@link fetchAllAuditEvents}), RBAC-gegated auf platform_admin
 * (`AUDIT_EXPORT`). Das CSV ist ein eigenstaendiger Pruef-Beleg (Bericht-Kopf +
 * flache Spalten, {@link serializeAuditCsv}); Geheimnisse nur redigiert. Ein
 * erreichter Sicherheits-Deckel wird im Kopf und per Toast offengelegt.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Download, FileText } from 'lucide-react';
import { Button } from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';
import { ROLE_GROUPS } from '@/types/auth';
import { EXPORT_EVENT_CAP, fetchAllAuditEvents } from '@/lib/api/audit';
import { serializeAuditCsv } from '../lib/audit-csv';
import type { AuditEvent, AuditQueryParams } from '@/types/audit';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** Menschenlesbare Beschreibung der Abfrage-Dimension fuer den CSV-Bericht-Kopf. */
export function describeDimension(query: AuditQueryParams, t: TFn): string {
  switch (query.mode) {
    case 'date':
      return `${t('filter.date')}: ${query.date ?? ''}`;
    case 'range':
      return `${t('filter.tab.time')}: ${query.from ?? ''} – ${query.to ?? ''}`;
    case 'actor':
      return `${t('filter.tab.actor')}: ${query.actorId ?? ''}`;
    case 'resource':
      return `${t('filter.tab.resource')}: ${query.resourceType ?? ''}#${query.resourceId ?? ''}`;
    default:
      return '';
  }
}

/** Aktive Zusatzfilter als komma-freier String (Semikolon-getrennt fuer den CSV-Kopf). */
export function describeFilters(query: AuditQueryParams, t: TFn): string {
  const parts: string[] = [];
  if (query.filters?.action) parts.push(`${t('filter.action')}=${query.filters.action}`);
  if (query.filters?.result) parts.push(`${t('filter.result')}=${t(`result.${query.filters.result}`)}`);
  return parts.join('; ');
}

/** Selbstbeschreibender Bericht-Kopf, den beide Formate (CSV-Kopf + JSON) tragen. */
export interface AuditExportReport {
  dimension: string;
  filters: string | null;
  generatedAt: string;
  count: number;
  truncated: boolean;
  cap: number;
}

/**
 * JSON-Export als selbstbeschreibendes Objekt `{ report, events }` — so traegt
 * auch ein abgeschnittener JSON-Beleg die Deckel-Offenlegung IN der Datei
 * (`report.truncated`), nicht nur als fluechtigen Toast (wie das CSV im Kopf).
 */
export function buildJsonExport(events: AuditEvent[], report: AuditExportReport): string {
  return JSON.stringify({ report, events }, null, 2);
}

function triggerDownload(content: string, mime: string, filename: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function AuditExport({ query }: { query: AuditQueryParams }) {
  const { t } = useTranslation('audit');
  const { hasRole } = useAuth();
  const [busy, setBusy] = useState<'json' | 'csv' | null>(null);

  if (!hasRole(ROLE_GROUPS.AUDIT_EXPORT)) return null;

  const run = async (format: 'json' | 'csv') => {
    setBusy(format);
    try {
      const { events, truncated } = await fetchAllAuditEvents(query);
      const generatedAt = new Date().toISOString();
      const filename = `audit-export-${query.mode}-${generatedAt.slice(0, 19).replace(/:/g, '-')}.${format}`;
      const report: AuditExportReport = {
        dimension: describeDimension(query, t),
        filters: describeFilters(query, t) || null,
        generatedAt,
        count: events.length,
        truncated,
        cap: EXPORT_EVENT_CAP,
      };

      if (format === 'json') {
        triggerDownload(buildJsonExport(events, report), 'application/json', filename);
      } else {
        const csv = serializeAuditCsv(events, {
          dimensionLabel: report.dimension,
          filters: report.filters ?? undefined,
          generatedAt,
          count: events.length,
          truncated,
          labels: {
            title: t('export.csvHeader.title'),
            dimension: t('export.csvHeader.dimension'),
            filter: t('export.csvHeader.filter'),
            generatedAt: t('export.csvHeader.generatedAt'),
            events: t('export.csvHeader.events'),
            truncatedNote: t('export.truncated', { cap: EXPORT_EVENT_CAP }),
          },
        });
        triggerDownload(csv, 'text/csv;charset=utf-8', filename);
      }

      if (truncated) toast.warning(t('export.truncated', { cap: EXPORT_EVENT_CAP }));
      else toast.success(t('export.success', { count: events.length }));
    } catch {
      toast.error(t('export.error'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex gap-2">
      <Button type="button" variant="outline" className="gap-2" onClick={() => run('json')} disabled={busy !== null}>
        <Download className="h-4 w-4" aria-hidden="true" />
        {t('export.json')}
      </Button>
      <Button type="button" variant="outline" className="gap-2" onClick={() => run('csv')} disabled={busy !== null}>
        <FileText className="h-4 w-4" aria-hidden="true" />
        {t('export.csv')}
      </Button>
    </div>
  );
}
